// Layer 2 (Engine): excursion ranking for control-room surfacing (optiflow-update.A1).
// Governance ranks — it never edits limits. See docs/engines/spc.md § Governance.

import type { IssueThread } from '@/versions/v1/schema/advisory-issue';
import type { Case } from '@/versions/v1/schema/case';
import type { ExcursionEvent } from '@/versions/v1/schema/spc';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { MetDiagnosticsPriority } from '@/versions/v1/schema/met-diagnostics-cause';
import type { Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import { UTILIZATION_RANKS } from '@/versions/v1/shared/bunker-hill-seed';
import { readingsAt } from '@/versions/v1/shared/series.derive';
import { utilisationPct } from '@/versions/v1/shared/reading.derive';
import { TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import {
  caseIdForVariable,
  subprocessFlowOrder,
  threadIdForVariable,
  variableIdForCase,
} from '@/versions/v1/shared/threads';
import { matchedRuleIdForCase } from '@/versions/v1/shared/RuleSetEngine';
import { ruleById } from '@/versions/v1/shared/diagnostic-rule.transform';
import { MET_DIAGNOSTICS_CAUSES } from '@/versions/v1/shared/bunker-hill-met-diagnostics-register';

export type GovernanceReason =
  | 'spec-breach'
  | 'limit-breach'
  | 'signal'
  | 'on-bottleneck'
  | 'upstream-of-bottleneck'
  | 'in-thread';

export type RankedExcursion = {
  excursion: ExcursionEvent;
  score: number;
  reasons: GovernanceReason[];
  threadId?: string;
  /** Paul's cause-level Priority (#271, `priority-likelihood`.A5) for the
   * excursion's matched rule's source cause(s) — every distinct figure among
   * them, ascending, when a rule names more than one cause, or `undefined`
   * when the excursion's case has no matched rule, or the rule names no
   * `sourceCauseId` at all. `undefined` is an honest absence, never coerced
   * to a worst-case number — a rule with no source cause is not
   * "unranked-lowest," it is simply outside this figure's coverage.
   *
   * Review `priority-likelihood` M2/ruling B: this is a **readout only**,
   * never a ranking key. `dr-zn-ph` names two causes in opposite directions
   * with different Priorities (MD-021 P1, MD-025 P3) — collapsing that to
   * one number via `min()` or any other reduction states a figure the data
   * does not support at current coverage, which is exactly what shipped
   * here before this fix. Never pick one, never average: every distinct
   * value the rule names is carried through and shown. */
  workbookPriorities?: readonly MetDiagnosticsPriority[];
};

const REASON_CHIP: Record<GovernanceReason, string> = {
  'spec-breach': 'spec',
  'limit-breach': 'limit',
  signal: 'run rule',
  'on-bottleneck': 'bottleneck',
  'upstream-of-bottleneck': 'upstream',
  'in-thread': 'thread',
};

export function reasonChipLabel(reason: GovernanceReason): string {
  return REASON_CHIP[reason];
}

/** The `workbook P<n>` reason chip for an excursion's resolved
 * `workbookPriorities` — shared by the Met feed (`DriftPanel.transform.ts`)
 * and the CRO panel (`RoleScreen.transform.ts`, review SHOULD-FIX 3) so the
 * two surfaces cannot drift apart on the label's shape. `undefined` when
 * nothing resolved — render no chip at all, never a `workbook P0` stand-in.
 * More than one distinct figure renders every one of them, joined, never
 * collapsed to a single number (see `RankedExcursion.workbookPriorities`). */
export function workbookPriorityChipLabel(
  priorities: readonly number[] | undefined,
): string | undefined {
  if (!priorities || priorities.length === 0) return undefined;
  return `workbook ${priorities.map((p) => `P${p}`).join('/')}`;
}

function bottleneckNode(state: SessionState): string {
  const readings = readingsAt(state);
  let bestId = 'sp-grind';
  let bestPct = -1;
  for (const rank of UTILIZATION_RANKS) {
    if (rank.scope !== 'subprocess') continue;
    const reading = rank.readingId ? readings.find((row) => row.id === rank.readingId) : undefined;
    const pct =
      (reading ? utilisationPct(reading) : undefined) ?? rank.utilizationPct ?? 0;
    if (pct > bestPct) {
      bestPct = pct;
      bestId = rank.id.startsWith('u-') ? `sp-${rank.id.slice(2)}` : rank.id;
    }
  }
  return bestId;
}

function nodeForExcursion(excursion: ExcursionEvent): string | undefined {
  const suffix = excursion.variableId.replace(/^mv-/, '');
  return TAG_MAPPING.find((row) => row.seriesId === `rs-${suffix}`)?.subprocessId;
}

function isUpstreamOfBottleneck(nodeId: string | undefined, bottleneck: string): boolean {
  if (!nodeId || nodeId === bottleneck) return false;
  const order = subprocessFlowOrder();
  const nodeIndex = order.indexOf(nodeId);
  const bottleneckIndex = order.indexOf(bottleneck);
  if (nodeIndex < 0 || bottleneckIndex < 0) return false;
  return nodeIndex < bottleneckIndex;
}

const SEVERITY_RANK = { strong: 0, signal: 1 } as const;

/** The excursion's case, found the same way `threadIdForVariable` does: the
 * conventional `case-<variableId>` id first, then a scan for whichever seeded
 * case resolves back to this variable via `variableIdForCase` (a case's own
 * id need not follow the convention — e.g. `case-ag-grade` for `mv-ag-grade`). */
function caseForVariable(variableId: string, cases: readonly Case[]): Case | undefined {
  const primaryId = caseIdForVariable(variableId);
  const primary = cases.find((row) => row.id === primaryId);
  if (primary) return primary;
  return cases.find((row) => variableIdForCase(row) === variableId);
}

/** Paul's Priority (#271) for whichever `MetDiagnosticsCause` row(s) the
 * excursion's matched rule names — every distinct figure among them,
 * ascending, never collapsed to one via `min()` or any other reduction.
 * `dr-zn-ph` names MD-021 (Priority 1) and MD-025 (Priority 3) — opposite
 * directions of the same loop, on purpose different figures — so an
 * excursion matched to it resolves `[1, 3]`, not `1`. `undefined`, never a
 * default, when the excursion has no resolvable case, the case has no
 * matched rule, or the rule carries no `sourceCauseId` — see
 * `RankedExcursion.workbookPriorities`. */
function workbookPrioritiesForExcursion(
  excursion: ExcursionEvent,
  cases: readonly Case[],
  diagnosis: Diagnosis | undefined,
): MetDiagnosticsPriority[] | undefined {
  const caseItem = caseForVariable(excursion.variableId, cases);
  if (!caseItem) return undefined;
  const ruleId = matchedRuleIdForCase(caseItem, diagnosis);
  if (!ruleId) return undefined;
  const rule = ruleById(ruleId);
  const causeIds = rule?.sourceCauseId;
  if (!causeIds || causeIds.length === 0) return undefined;
  const distinct = new Set<MetDiagnosticsPriority>();
  for (const causeId of causeIds) {
    const cause = MET_DIAGNOSTICS_CAUSES.find((row) => row.id === causeId);
    if (cause) distinct.add(cause.priority);
  }
  if (distinct.size === 0) return undefined;
  return [...distinct].sort((a, b) => a - b);
}

export function rankExcursions(
  excursionList: readonly ExcursionEvent[],
  state: SessionState,
  threads: readonly IssueThread[],
  cases: readonly Case[] = [],
  diagnosis?: Diagnosis,
): RankedExcursion[] {
  const bottleneck = bottleneckNode(state);
  const caseIdsInThreads = new Map<string, string>();
  for (const thread of threads) {
    for (const caseId of thread.caseIds) {
      caseIdsInThreads.set(caseId, thread.id);
    }
  }

  const ranked = excursionList.map((excursion) => {
    const reasons: GovernanceReason[] = [];
    let score = 0;
    if (excursion.conforming === false) {
      score += 3;
      reasons.push('spec-breach');
    } else if (excursion.rule === 'beyond-limit') {
      score += 2;
      reasons.push('limit-breach');
    } else {
      score += 1;
      reasons.push('signal');
    }
    const node = nodeForExcursion(excursion);
    if (node === bottleneck) {
      score += 2;
      reasons.push('on-bottleneck');
    } else if (isUpstreamOfBottleneck(node, bottleneck)) {
      score += 1;
      reasons.push('upstream-of-bottleneck');
    }
    const caseId = caseIdForVariable(excursion.variableId);
    const threadId =
      caseIdsInThreads.get(caseId) ??
      threadIdForVariable(excursion.variableId, threads, cases);
    if (threadId) {
      score += 1;
      reasons.push('in-thread');
    }
    const workbookPriorities = workbookPrioritiesForExcursion(excursion, cases, diagnosis);
    return { excursion, score, reasons, threadId, workbookPriorities };
  });

  // #239's own keys — score desc, then severity — are the whole ranking.
  // Review priority-likelihood M2/ruling B: a workbook-Priority tie-break
  // used to sit below these two keys. It is removed. At current rule-set
  // coverage only two variables ever resolve a workbook figure at all, both
  // to the same value, so the key could never separate anything it touched
  // — and that inertness is what let a modelling error (collapsing
  // dr-zn-ph's two different-direction causes to one number via `min()`)
  // ship undetected. `workbookPriorities` is carried on each ranked row as a
  // **readout only** (rendered as a `workbook P<n>` chip, `#271` is read,
  // not ranked, by displaying it) — see `RankedExcursion.workbookPriorities`.
  ranked.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    const sev =
      SEVERITY_RANK[a.excursion.severity] - SEVERITY_RANK[b.excursion.severity];
    if (sev !== 0) return sev;
    return b.excursion.detectedAt.localeCompare(a.excursion.detectedAt);
  });

  return ranked;
}

/** One row per variable — merge duplicate labels; combine chips; raw length still drives `moreCount`. */
export function dedupeRankedExcursions(ranked: readonly RankedExcursion[]): RankedExcursion[] {
  const seenId = new Set<string>();
  const byVariable = new Map<string, RankedExcursion>();
  const out: RankedExcursion[] = [];
  for (const row of ranked) {
    if (seenId.has(row.excursion.id)) continue;
    seenId.add(row.excursion.id);
    const key = row.excursion.variableId;
    const existing = byVariable.get(key);
    if (existing) {
      const mergedReasons = [...new Set([...existing.reasons, ...row.reasons])];
      const merged: RankedExcursion = {
        ...existing,
        score: Math.max(existing.score, row.score),
        reasons: mergedReasons,
      };
      byVariable.set(key, merged);
      const idx = out.indexOf(existing);
      if (idx >= 0) out[idx] = merged;
      continue;
    }
    byVariable.set(key, row);
    out.push(row);
  }
  return out;
}

export function topN(
  ranked: readonly RankedExcursion[],
  n: number,
): { shown: RankedExcursion[]; more: RankedExcursion[] } {
  return {
    shown: ranked.slice(0, n),
    more: ranked.slice(n),
  };
}
